/* global Messenger */
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import uuid4 from 'uuid4';

import { uiConfig } from '../../../../../../../conf/predicateParts';

// Components
import Form from '../../../../../../../../components/Form';
import { DrawerContainer, DrawerContent } from '../../../../../../../../components/Drawer';
import motionProps from '../../../../../../../../components/Drawer/motion';
import FeedbackCard from '../../../../../../../../components/FeedbackCard';
import { convertDateToISOString } from '../../../../../../../../components/DatePicker';
import { Spinner } from '../../../../../../../../components/Spinner';
import { PredicatePartRelForm } from './PredicatePartRelForm';

// Context
import { CriteriaStoreContext } from '../../../../../../state/criteria/context';

// Enums
import { dtoState } from '../../../../../../../../common/enums/dtoState';

// Hooks
import { useFetchCriteriaResources } from '../../../../../../api/hooks/useFetchCriteriaResources';
import { useGetCriteriaPredicatePartMetadata } from '../../../../../../api/hooks/useGetCriteriaPredicatePartMetadata';
import { useGetPredicatePartRelsMetadata } from '../../../../../../api/hooks/useGetPredicatePartRelsMetadata';
import { usePredicateStorePredicate } from '../../../../../../state/predicate/hooks/usePredicateStorePredicate';
import { useNextPredicatePartOrder } from '../../../../../../state/predicate/hooks/useNextPredicatePartOrder';

export const SpinnerCont = styled.div`
    display: flex;
    justify-content: center;
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 100vh;
`;

const DrawerRow = `
    padding: 1rem;
    display: flex;
    flex-direction: row;
`;

export const DrawerHeader = styled.div`
    ${DrawerRow};
    justify-content: space-between;
`;

export const DrawerFooter = styled.div`
    ${DrawerRow};
    justify-content: flex-end;
`;

const PredicatePartEditorBase = ({
    formModel,
    predicatePart,
    selectedPredicatePartUuid,
    closeDrawer,
    saveDisabled,
    onMount,
    onSave,
    isExistingDoc,
    isOpen
}) => {
    const [partState, setPartState] = React.useState({ ...predicatePart });
    const { sessionToken } = React.useContext(CriteriaStoreContext);
    const theme = React.useContext(ThemeContext);
    const PredicatePartEditorRef = React.useRef(null);
    const MainFormRef = React.useRef(null);
    const PredicatePartRelFormRef = React.useRef(null);
    const isNew = React.useMemo(() => selectedPredicatePartUuid === 'new', [selectedPredicatePartUuid]);
    const [conceptSelectionType, setConceptSelectionType] = React.useState(null);
    const clearSelectedConcepts = React.useCallback(() => {
        const updatedPredicatePartConcepts = (partState?.predicatePartConcepts ?? [])
            .filter(({ dtoState }) => dtoState !== dtoState.New)
            .map((concept) => ({ ...concept, dtoState: dtoState.Deleted }));

        setPartState({
            ...partState,
            predicatePartConcepts: updatedPredicatePartConcepts
        });
    }, [partState, setPartState]);
    const applyPredicatePartConceptsUpdate = React.useCallback(
        (data = []) => {
            const newStateConceptUuids = data.map((item) => item.uuid);
            const existingDeletedConcepts = (partState.predicatePartConcepts ?? []).filter(
                (concept) => concept.dtoState === dtoState.Deleted && !newStateConceptUuids.includes(concept.uuid)
            );
            const updatedConcepts = [...data, ...existingDeletedConcepts];

            setPartState({
                ...partState,
                predicatePartConcepts: updatedConcepts,
                dtoState: partState?.dtoState === dtoState.New ? dtoState.New : dtoState.Updated
            });
            setEdited(true);
        },
        [partState, setPartState, setEdited]
    );

    const updateConceptSelectionType = React.useCallback(
        (newVal, clearSelection = false) => {
            setConceptSelectionType(newVal);
            if (clearSelection) {
                clearSelectedConcepts();
            }
        },
        [setConceptSelectionType, clearSelectedConcepts]
    );
    const [buttonClicked, setButtonClicked] = React.useState();
    const CloseDrawer = () => {
        return typeof closeDrawer === 'function' ? closeDrawer() : null;
    };
    const criteriaPredicatePartMetadataQuery = useGetCriteriaPredicatePartMetadata({
        sessionToken,
        partId: predicatePart?.partId
    });
    const predicatePartRelsMetadataQuery = useGetPredicatePartRelsMetadata(sessionToken);
    const criteriaResourcesQuery = useFetchCriteriaResources({ sessionToken });
    const [compose, updateCompose] = React.useState(false);
    const [edited, setEdited] = React.useState(false);
    const [errors, setErrors] = React.useState({});
    const [fatalError, setFatalError] = React.useState(null);
    const [isSaving, setIsSaving] = React.useState(false);
    const [classType, setClassType] = React.useState('');
    const [partType, setPartType] = React.useState('');
    const [resourceType, setResourceType] = React.useState('');
    const [resource, setResource] = React.useState('');
    const [resourceParam, setResourceParam] = React.useState('');
    const [dataInputNodeDTO, setDataInputNodeDTO] = React.useState(null);
    const onSaveCb = React.useCallback(
        (data, closeEditor) => {
            setFatalError(null);
            setEdited(false);
            onSave(data, isNew);
            setTimeout(() => {
                setIsSaving(false);
                Messenger.showMessage({
                    type: `info`,
                    title: 'Changes have been applied locally.',
                    message: `In order to save these changes please click save/apply on the predicate form.`
                });
                if (closeEditor) {
                    setTimeout(() => {
                        CloseDrawer();
                    }, 500);
                }
            }, 500);
        },
        [setIsSaving, setEdited, setFatalError, onSave, isNew]
    );

    const prepareDocument = (docState, newPredicate) => {
        const formattedValues = {};

        if (typeof docState?.dataInputNodeDTO !== 'undefined') {
            formattedValues.dataInputNodeDTO = (
                criteriaPredicatePartMetadataQuery?.data?.options?.dataInputNodeDTO ?? []
            ).find(({ nodeId }) => nodeId === docState?.dataInputNodeDTO);
        }
        ['dataInputDate1', 'dataInputDate2'].forEach((dateProp) => {
            if (
                typeof docState?.[dateProp] !== 'undefined' &&
                docState[dateProp] !== '' &&
                docState[dateProp] instanceof Date
            ) {
                formattedValues[dateProp] = convertDateToISOString(docState[dateProp]);
            }
        });
        if (typeof docState?.criteriaResourceDTO !== 'undefined') {
            formattedValues.criteriaResourceDTO = (criteriaResourcesQuery?.data ?? []).find(
                ({ resourceId }) => resourceId === resource
            );
        }
        if (typeof docState?.criteriaResourceParamDTO !== 'undefined') {
            const targetResourceParam = (formattedValues.criteriaResourceDTO?.criteriaResourceParams ?? []).find(
                ({ paramId }) => paramId === resourceParam
            );

            formattedValues.criteriaResourceParamDTO = targetResourceParam ?? undefined;
        }

        return {
            ...docState,
            ...formattedValues,
            dtoState: newPredicate || docState.dtoState === dtoState.New ? dtoState.New : dtoState.Updated
        };
    };

    const onApplyFormState = (formState) => {
        const predicatePartRels = [];

        if (PredicatePartRelFormRef?.current !== null) {
            const predicatePartRel = PredicatePartRelFormRef?.current?.getFormState?.();

            if (!(predicatePartRel?.dtoState === dtoState.New && !predicatePartRel?.constraintType)) {
                predicatePartRels.push(PredicatePartRelFormRef?.current?.getFormState?.());
            }
        }

        if (saveDisabled) {
            console.warn('Authoring Interface is currently read-only.');
            return;
        }

        let update = prepareDocument(
            {
                ...predicatePart,
                ...formState,
                dataInputNodeDTO: dataInputNodeDTO,
                predicatePartRels,
                predicatePartConcepts: partState?.predicatePartConcepts ?? [],
                dtoState: [dtoState.Updated, dtoState.New].includes(partState?.dtoState)
                    ? partState?.dtoState
                    : formState?.dtoState
            },
            isNew
        );
        updateCompose(false);
        let { valid } = validate(update);

        if (!valid) {
            setIsSaving(false);
            return;
        }
        if (isExistingDoc(update)) {
            setFatalError('This part already exists in the system.');
            return;
        }

        onSaveCb(update, buttonClicked === 'save');
    };

    const isLoading = React.useMemo(
        () => isSaving || criteriaPredicatePartMetadataQuery.isLoading || predicatePartRelsMetadataQuery.isLoading,
        [isSaving, criteriaPredicatePartMetadataQuery, predicatePartRelsMetadataQuery]
    );

    // Temporary validation solution
    const validate = (formState) => {
        let newErrors = {};
        for (let field of formModel) {
            let { valid, failed } = field.validation(formState[field.accessor]);
            if (!valid) {
                newErrors[field.accessor] = 'Invalid Input: ' + failed.join(', ');
            }
            // Add additional validation here;
        }
        // Add relational validation here

        setErrors(newErrors);
        let valid = Object.keys(newErrors).length === 0;
        return { valid, failureMsg: '' };
    };

    const reduceResourcesToMap = (resources = [], key = '') => {
        return resources.reduce((acc, data) => {
            return {
                ...acc,
                [data?.[key]]: data?.name
            };
        }, {});
    };

    const sortedResources = React.useMemo(() => {
        if (criteriaResourcesQuery?.data?.length) {
            return criteriaResourcesQuery?.data.reduce((acc, curr) => {
                if (typeof acc?.[curr.resourceType] === 'undefined') {
                    acc[curr.resourceType] = [];
                }

                acc[curr.resourceType].push(curr);

                return acc;
            }, {});
        }

        return {};
    }, [criteriaResourcesQuery?.data]);

    const setResourceOptions = (type) => {
        let subIndex = formModel.findIndex((v) => v.accessor === 'criteriaResourceDTO');
        if (subIndex !== -1 && type && sortedResources) {
            formModel[subIndex].options = reduceResourcesToMap((sortedResources ?? {})[type], 'resourceId');
        }
    };

    const setResourceParamOptions = (resourceId) => {
        let subIndex = formModel.findIndex((v) => v.accessor === 'criteriaResourceParamDTO');
        if (subIndex !== -1 && sortedResources) {
            const targetResource = (criteriaResourcesQuery?.data ?? []).find((resourceOption) => {
                return resourceOption.resourceId === resourceId;
            });
            formModel[subIndex].options = targetResource
                ? reduceResourcesToMap([...(targetResource?.criteriaResourceParams ?? [])], 'paramId')
                : {};
        }
    };

    const prepareOptions = () => {
        let optionsOverride = {};
        optionsOverride.partType = criteriaPredicatePartMetadataQuery?.data?.enumMaps?.partType;
        optionsOverride.dataInputClassType = Object.entries(
            criteriaPredicatePartMetadataQuery?.data?.enumMaps?.dataInputClassType ?? {}
        ).reduce(
            (acc, [key, value]) => [
                ...acc,
                {
                    label: key,
                    value,
                    disabled: ['Complex', 'Enumeration'].includes(key)
                }
            ],
            []
        );
        optionsOverride.conceptSelectionType = criteriaPredicatePartMetadataQuery?.data?.enumMaps?.conceptSelectionType;
        optionsOverride.dataInputNodeDTO = criteriaPredicatePartMetadataQuery?.data?.enumMaps?.dataInputNodeDTO;
        optionsOverride.resourceType = criteriaPredicatePartMetadataQuery?.data?.enumMaps?.resourceType;
        if (resourceType) {
            setResourceOptions(resourceType);
        }
        if (resource) {
            setResourceParamOptions(resource);
        }

        return optionsOverride;
    };

    let optionsOverride = prepareOptions();

    for (let key in optionsOverride) {
        let subIndex = formModel.findIndex((v) => v.accessor === key);
        if (subIndex !== -1) {
            formModel[subIndex].options = optionsOverride[key];
        }
    }

    const hookFxns = {
        updateConceptSelectionType: updateConceptSelectionType,
        updateClassTypeFn: setClassType,
        updatePartType: setPartType,
        updateDataInputNodeDTO: setDataInputNodeDTO,
        updateResourceType: (type, isInit) => {
            setResourceType(type);
            if (!isInit) {
                // MainFormRef?.current?.updateFormState('criteriaResourceDTO', null, false);
                hookFxns.updateResource(null, false);
            }
            // setResourceOptions(type);
        },
        updateResource: (selectedResource) => {
            setResource(selectedResource);
        },
        maybeUpdateResourceOptionsMap: (type) => {
            setResourceOptions(type);
        },
        maybeUpdateResourceParamOptionsMap: (selectedResource) => {
            setResourceParamOptions(selectedResource);
        },
        updateResourceParam: setResourceParam
    };
    const onRequestClose = React.useCallback(() => {
        if (edited) {
            PredicatePartEditorRef?.current?.setShowDiscardChangesModal(true);
        } else {
            CloseDrawer();
        }
    }, [edited, PredicatePartEditorRef, CloseDrawer]);

    return (
        <DrawerContainer
            open={isOpen}
            width="75vw"
            push={{ distance: 64 }}
            onClose={() => {
                onRequestClose();
            }}
            placement="right"
            rootClassName="level-1"
            className="drawer2"
            zIndex={9999}
            onKeyDown={(e) => {
                if (e?.keyCode === 27) onRequestClose();
            }}
            {...motionProps}
        >
            <DrawerContent
                open={isOpen}
                ref={PredicatePartEditorRef}
                title={`${isNew ? 'Add' : 'Edit'} Part`}
                saveDisabled={!edited || saveDisabled}
                onSave={() => {
                    setIsSaving(true);
                    setButtonClicked('save');
                    updateCompose(true);
                }}
                onApply={() => {
                    setIsSaving(true);
                    setButtonClicked('apply');
                    updateCompose(true);
                }}
                closeFn={onRequestClose}
                closeDrawer={CloseDrawer}
                isLoading={isLoading}
                headerColor={theme.colors.brand.P7}
            >
                <div style={{ padding: '1rem' }}>
                    {criteriaPredicatePartMetadataQuery.isLoading || predicatePartRelsMetadataQuery.isLoading ? (
                        <SpinnerCont>
                            <Spinner size={'2.3rem'} color={'#1E527E'} />
                        </SpinnerCont>
                    ) : (
                        <>
                            {fatalError != null && (
                                <FeedbackCard
                                    intent={'danger'}
                                    feedback={{
                                        heading: `An error has occurred.`,
                                        message:
                                            typeof fatalError === 'string'
                                                ? fatalError
                                                : `Please wait a few moments before trying your request
again.`
                                    }}
                                    details={fatalError}
                                />
                            )}
                            {isNew ? 'Enter' : 'Adjust'} required information below.
                            <p style={{ margin: `1.33rem 0 0 0` }}></p>
                            {predicatePart ? (
                                <Form
                                    ref={MainFormRef}
                                    formModel={formModel}
                                    isNew={isNew}
                                    data={predicatePart}
                                    errors={errors}
                                    compose={compose}
                                    onApplyFormState={onApplyFormState}
                                    setEdited={setEdited}
                                    fatalError={fatalError}
                                    onMount={onMount}
                                    menuPortalDocument
                                    compact
                                    hookFxns={hookFxns}
                                />
                            ) : null}
                            {partType === 'DataInput' && classType === 'Concept' ? (
                                <PredicatePartRelForm
                                    ref={PredicatePartRelFormRef}
                                    data={predicatePart?.predicatePartRels?.[0]}
                                    setEdited={setEdited}
                                    conceptSelectionType={conceptSelectionType}
                                    selectedConcepts={partState?.predicatePartConcepts ?? []}
                                    clearSelectedConcepts={clearSelectedConcepts}
                                    applyPredicatePartConceptsUpdate={applyPredicatePartConceptsUpdate}
                                    partId={predicatePart?.partId}
                                />
                            ) : null}
                        </>
                    )}
                </div>
            </DrawerContent>
        </DrawerContainer>
    );
};

const ExistingPredicatePartEditor = (props) => {
    const predicatePart = usePredicateStorePredicate(props.selectedPredicatePartUuid);

    return <PredicatePartEditorBase {...props} predicatePart={predicatePart} />;
};

const NewPredicatePartEditor = (props) => {
    const newPartOrder = useNextPredicatePartOrder({
        parentUuid: props.parentPredicateUuid
    });
    return (
        <PredicatePartEditorBase
            {...props}
            isNew
            predicatePart={{ ...uiConfig.newDoc, predicatePartOrder: newPartOrder, uuid: uuid4() }}
        />
    );
};

export const PredicatePartEditor = (props) => {
    if (!props.selectedPredicatePartUuid) return null;
    return props.selectedPredicatePartUuid === 'new' ? (
        <NewPredicatePartEditor {...props} />
    ) : (
        <ExistingPredicatePartEditor {...props} />
    );
};

export default PredicatePartEditor;

PredicatePartEditor.propTypes = {
    parentPredicateUuid: PropTypes.string,
    selectedPredicatePartUuid: PropTypes.string,
    formModel: PropTypes.arrayOf(PropTypes.object),
    closeDrawer: PropTypes.func,
    isExistingDoc: PropTypes.func,
    existingPartCount: PropTypes.number,
    onMount: PropTypes.func,
    onSave: PropTypes.func,
    saveDisabled: PropTypes.bool,
    isOpen: PropTypes.bool
};
